import React, { useState, useEffect, useRef } from 'react';
import { propertyAPI, bookingTemplateAPI, developerTemplateAPI } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import { Building2, Plus, Edit, Trash2, Upload, Image, FileText, X, Eye, ClipboardList, Link2, Shield, MapPin } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminProperties = () => {
  const [properties, setProperties] = useState([]);
  const [bookingTemplates, setBookingTemplates] = useState([]);
  const [developerTemplates, setDeveloperTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const imageInputRef = useRef(null);
  const brochureInputRef = useRef(null);

  const defaultForm = {
    name: '',
    description: '',
    location: '',
    address: '',
    developer: '',
    property_type: 'condominium',
    total_units: '',
    price_range_min: '',
    price_range_max: '',
    is_exclusive: true,
    images: [],
    brochure_url: null,
    booking_template_id: '',
    developer_template_id: '',
    amenities: [],
    commission_percentage: '3',
    agent_commission_split: { agent: '40', rm: '45', avp: '50', svp: '55', evp: '60' },
    customer_cashback_percentage: '0.5',
    introducer_commission_percentage: '0.5',
    platform_fee_percentage: '0.5',
    total_booking_fee: '',
  };

  const [formData, setFormData] = useState(defaultForm);
  const [amenitiesInput, setAmenitiesInput] = useState('');
  const [pendingImages, setPendingImages] = useState([]);
  const [pendingBrochure, setPendingBrochure] = useState(null);

  useEffect(() => {
    loadProperties();
    loadBookingTemplates();
    loadDeveloperTemplates();
  }, []);

  const loadProperties = async () => {
    try {
      const res = await propertyAPI.getProperties();
      setProperties(res.data);
    } catch (error) {
      console.error('Error loading properties:', error);
      toast.error('Failed to load properties');
    } finally {
      setLoading(false);
    }
  };

  const loadBookingTemplates = async () => {
    try {
      const res = await bookingTemplateAPI.getTemplates();
      setBookingTemplates(res.data || []);
    } catch (error) {
      console.error('Error loading booking templates:', error);
    }
  };

  const loadDeveloperTemplates = async () => {
    try {
      const res = await developerTemplateAPI.getTemplates();
      setDeveloperTemplates(res.data || []);
    } catch (error) {
      console.error('Error loading developer templates:', error);
    }
  };

  const getTemplateName = (templateId) => {
    const template = bookingTemplates.find(t => t.id === templateId);
    return template?.name || 'Default';
  };

  const getDeveloperTemplateName = (templateId) => {
    const template = developerTemplates.find(t => t.id === templateId);
    return template ? `${template.name} (${template.field_count || 0} fields)` : null;
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.location || !formData.developer) {
      toast.error('Please fill in all required fields');
      return;
    }

    setUploading(true);
    try {
      const data = {
        name: formData.name,
        description: formData.description,
        location: formData.location,
        address: formData.address || null,
        developer: formData.developer,
        property_type: formData.property_type,
        total_units: parseInt(formData.total_units) || 0,
        price_range_min: parseFloat(formData.price_range_min) || 0,
        price_range_max: parseFloat(formData.price_range_max) || 0,
        is_exclusive: formData.is_exclusive,
        commission_percentage: parseFloat(formData.commission_percentage) || 3,
        customer_cashback_percentage: parseFloat(formData.customer_cashback_percentage) || 0.5,
        introducer_commission_percentage: parseFloat(formData.introducer_commission_percentage) || 0.5,
        platform_fee_percentage: parseFloat(formData.platform_fee_percentage) || 0.5,
        total_booking_fee: formData.total_booking_fee ? parseFloat(formData.total_booking_fee) : null,
        agent_commission_split: Object.fromEntries(
          Object.entries(formData.agent_commission_split).map(([k, v]) => [k, parseFloat(v) || 50])
        ),
        amenities: amenitiesInput.split(',').map(a => a.trim()).filter(a => a),
        images: formData.images || [],
        brochure_url: formData.brochure_url,
        booking_template_id: formData.booking_template_id || null,
        developer_template_id: formData.developer_template_id || null,
      };

      let propertyId;
      
      if (editingProperty) {
        await propertyAPI.updateProperty(editingProperty.id, data);
        propertyId = editingProperty.id;
        toast.success('Property updated');
      } else {
        const res = await propertyAPI.createProperty(data);
        propertyId = res.data.id;
        toast.success('Property created');
      }

      // Upload pending images
      if (pendingImages.length > 0 && propertyId) {
        try {
          await propertyAPI.uploadImages(propertyId, pendingImages);
          toast.success(`${pendingImages.length} image(s) uploaded`);
        } catch (err) {
          toast.error('Failed to upload some images');
        }
      }

      // Upload pending brochure
      if (pendingBrochure && propertyId) {
        try {
          await propertyAPI.uploadBrochure(propertyId, pendingBrochure);
          toast.success('Brochure uploaded');
        } catch (err) {
          toast.error('Failed to upload brochure');
        }
      }
      
      closeModal();
      loadProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save property');
    } finally {
      setUploading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProperty(null);
    setFormData(defaultForm);
    setAmenitiesInput('');
    setPendingImages([]);
    setPendingBrochure(null);
  };

  const handleEdit = (property) => {
    setEditingProperty(property);
    setFormData({
      ...defaultForm,
      ...property,
      is_exclusive: property.is_exclusive !== false,
      address: property.address || '',
      total_booking_fee: property.total_booking_fee?.toString() || '',
      commission_percentage: property.commission_percentage?.toString() || '3',
      customer_cashback_percentage: property.customer_cashback_percentage?.toString() || '0.5',
      introducer_commission_percentage: property.introducer_commission_percentage?.toString() || '0.5',
      platform_fee_percentage: property.platform_fee_percentage?.toString() || '0.5',
      total_units: property.total_units?.toString() || '',
      price_range_min: property.price_range_min?.toString() || '',
      price_range_max: property.price_range_max?.toString() || '',
      booking_template_id: property.booking_template_id || '',
      developer_template_id: property.developer_template_id || '',
      agent_commission_split: Object.fromEntries(
        Object.entries(property.agent_commission_split || defaultForm.agent_commission_split).map(([k, v]) => [k, v?.toString() || '50'])
      ),
    });
    setAmenitiesInput(property.amenities?.join(', ') || '');
    setPendingImages([]);
    setPendingBrochure(null);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this property? This will also delete all units.')) return;
    try {
      await propertyAPI.deleteProperty(id);
      toast.success('Property deleted');
      loadProperties();
    } catch (error) {
      toast.error('Failed to delete property');
    }
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setPendingImages(prev => [...prev, ...files]);
    }
  };

  const handleBrochureSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPendingBrochure(file);
    }
  };

  const removePendingImage = (index) => {
    setPendingImages(prev => prev.filter((_, i) => i !== index));
  };

  const removeExistingImage = async (imageIndex) => {
    if (!editingProperty) return;
    if (!window.confirm('Remove this image?')) return;
    
    try {
      await propertyAPI.deleteImage(editingProperty.id, imageIndex);
      const newImages = [...formData.images];
      newImages.splice(imageIndex, 1);
      setFormData({ ...formData, images: newImages });
      toast.success('Image removed');
    } catch (error) {
      toast.error('Failed to remove image');
    }
  };

  const removeExistingBrochure = async () => {
    if (!editingProperty) return;
    if (!window.confirm('Remove this brochure?')) return;
    
    try {
      await propertyAPI.deleteBrochure(editingProperty.id);
      setFormData({ ...formData, brochure_url: null });
      toast.success('Brochure removed');
    } catch (error) {
      toast.error('Failed to remove brochure');
    }
  };

  const propertyTypes = [
    { value: 'condominium', label: 'Condominium' },
    { value: 'apartment', label: 'Apartment' },
    { value: 'landed', label: 'Landed House' },
    { value: 'commercial', label: 'Commercial' },
    { value: 'industrial', label: 'Industrial' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-properties">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Property Management</h1>
          <p className="text-gray-500 mt-1">Manage property listings and details</p>
        </div>
        <Dialog open={showModal} onOpenChange={(open) => {
          if (!open) closeModal();
          else setShowModal(true);
        }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="add-property-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Property
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-gray-900 text-xl">
                {editingProperty ? 'Edit Property' : 'Add New Property'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-6 mt-4">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-800 border-b pb-2">Basic Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label className="text-gray-700">Property Name *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1"
                      placeholder="e.g., Skyline Residences"
                      data-testid="property-name"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-gray-700">Description</Label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="mt-1"
                      placeholder="Property description..."
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Location *</Label>
                    <Input
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="mt-1"
                      placeholder="e.g., Mont Kiara, KL"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Developer *</Label>
                    <Input
                      value={formData.developer}
                      onChange={(e) => setFormData({ ...formData, developer: e.target.value })}
                      className="mt-1"
                      placeholder="e.g., Skyline Dev Sdn Bhd"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-gray-700 flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      Full Address
                    </Label>
                    <Textarea
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="mt-1"
                      placeholder="e.g., Lot 123, Jalan Taman Bestari, 81300 Skudai, Johor, Malaysia"
                      rows={2}
                      data-testid="property-address"
                    />
                    <p className="text-xs text-gray-400 mt-1">This address will be used to display the property location on the map</p>
                  </div>
                  <div>
                    <Label className="text-gray-700">Property Type</Label>
                    <Select
                      value={formData.property_type}
                      onValueChange={(v) => setFormData({ ...formData, property_type: v })}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {propertyTypes.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-gray-700">Total Units</Label>
                    <Input
                      type="number"
                      value={formData.total_units}
                      onChange={(e) => setFormData({ ...formData, total_units: e.target.value })}
                      className="mt-1"
                      placeholder="e.g., 200"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Min Price (RM)</Label>
                    <Input
                      type="number"
                      value={formData.price_range_min}
                      onChange={(e) => setFormData({ ...formData, price_range_min: e.target.value })}
                      className="mt-1"
                      placeholder="e.g., 500000"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Max Price (RM)</Label>
                    <Input
                      type="number"
                      value={formData.price_range_max}
                      onChange={(e) => setFormData({ ...formData, price_range_max: e.target.value })}
                      className="mt-1"
                      placeholder="e.g., 1500000"
                    />
                  </div>
                </div>
              </div>

              {/* Exclusive / Non-Exclusive Toggle */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-800 border-b pb-2 flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Project Exclusivity
                </h3>
                <div
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    formData.is_exclusive
                      ? 'border-[#4E50FF] bg-[#4E50FF]/5'
                      : 'border-gray-200 bg-gray-50'
                  }`}
                  onClick={() => setFormData({ ...formData, is_exclusive: !formData.is_exclusive })}
                  data-testid="exclusive-toggle"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-6 rounded-full relative transition-colors ${formData.is_exclusive ? 'bg-[#4E50FF]' : 'bg-gray-300'}`}>
                        <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${formData.is_exclusive ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900">
                          {formData.is_exclusive ? 'Exclusive Project' : 'Non-Exclusive Project'}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {formData.is_exclusive
                            ? 'Unit availability is linked — agents select from available units when booking'
                            : 'Shared project — agents manually enter unit number in booking form'}
                        </p>
                      </div>
                    </div>
                    <Badge className={formData.is_exclusive ? 'bg-[#4E50FF] text-white' : 'bg-gray-200 text-gray-600'}>
                      {formData.is_exclusive ? 'Exclusive' : 'Non-Exclusive'}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Booking Template Link */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-800 border-b pb-2 flex items-center gap-2">
                  <Link2 className="w-5 h-5" />
                  Booking Form Template
                </h3>
                <div>
                  <Label className="text-gray-700">Select Booking Form Template</Label>
                  <p className="text-xs text-gray-500 mb-2">
                    This template will be used when creating bookings for this property. Property info will be auto-filled.
                  </p>
                  <Select
                    value={formData.booking_template_id || 'none'}
                    onValueChange={(v) => setFormData({ ...formData, booking_template_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select a booking template" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">
                        <span className="text-gray-500">Use Default Template</span>
                      </SelectItem>
                      {bookingTemplates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          <div className="flex items-center gap-2">
                            <ClipboardList className="w-4 h-4 text-blue-500" />
                            {template.name}
                            {template.pdf_template_file && (
                              <Badge variant="outline" className="text-xs ml-2">Has PDF</Badge>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="mt-3">
                  <Label className="text-gray-700">Developer PDF Template</Label>
                  <p className="text-xs text-gray-500 mb-2">
                    The developer's original booking form. AI will detect fields for customers to fill in.
                  </p>
                  <Select
                    value={formData.developer_template_id || 'none'}
                    onValueChange={(v) => setFormData({ ...formData, developer_template_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger className="mt-1" data-testid="developer-template-select">
                      <SelectValue placeholder="Select developer template" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">
                        <span className="text-gray-500">No Developer Template</span>
                      </SelectItem>
                      {developerTemplates.map((template) => (
                        <SelectItem key={template.id} value={template.id}>
                          <div className="flex items-center gap-2">
                            <FileText className="w-4 h-4 text-purple-500" />
                            {template.name}
                            {template.ai_detection_status === 'completed' && (
                              <Badge variant="outline" className="text-xs ml-2 bg-purple-50 text-purple-600">{template.field_count} fields</Badge>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Images Upload */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-800 border-b pb-2 flex items-center gap-2">
                  <Image className="w-5 h-5" />
                  Property Images
                </h3>
                
                <input
                  type="file"
                  ref={imageInputRef}
                  onChange={handleImageSelect}
                  accept="image/*"
                  multiple
                  className="hidden"
                />
                
                <div 
                  onClick={() => imageInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                >
                  <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-600">Click to upload images</p>
                  <p className="text-xs text-gray-400 mt-1">PNG, JPG, WEBP up to 10MB each</p>
                </div>

                {/* Existing Images */}
                {formData.images && formData.images.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600 font-medium">Existing Images ({formData.images.length})</p>
                    <div className="grid grid-cols-4 gap-2">
                      {formData.images.map((img, idx) => (
                        <div key={idx} className="relative group">
                          <img
                            src={img.startsWith('http') ? img : `${BACKEND_URL}${img}`}
                            alt={`Property ${idx + 1}`}
                            className="w-full h-20 object-cover rounded border"
                          />
                          {editingProperty && (
                            <button
                              type="button"
                              onClick={() => removeExistingImage(idx)}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Pending Images */}
                {pendingImages.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600 font-medium">New Images to Upload ({pendingImages.length})</p>
                    <div className="grid grid-cols-4 gap-2">
                      {pendingImages.map((file, idx) => (
                        <div key={idx} className="relative group">
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`New ${idx + 1}`}
                            className="w-full h-20 object-cover rounded border border-blue-300"
                          />
                          <button
                            type="button"
                            onClick={() => removePendingImage(idx)}
                            className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                          <Badge className="absolute bottom-1 left-1 text-xs bg-blue-500">New</Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Brochure Upload */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-800 border-b pb-2 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Property Brochure
                </h3>
                
                <input
                  type="file"
                  ref={brochureInputRef}
                  onChange={handleBrochureSelect}
                  accept=".pdf,image/*"
                  className="hidden"
                />

                {/* Show existing brochure */}
                {formData.brochure_url && !pendingBrochure && (
                  <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <FileText className="w-8 h-8 text-green-600" />
                    <div className="flex-1">
                      <p className="font-medium text-green-800">Brochure Uploaded</p>
                      <a 
                        href={formData.brochure_url.startsWith('http') ? formData.brochure_url : `${BACKEND_URL}${formData.brochure_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-green-600 hover:underline flex items-center gap-1"
                      >
                        <Eye className="w-3 h-3" /> View Brochure
                      </a>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => brochureInputRef.current?.click()}
                      >
                        Replace
                      </Button>
                      {editingProperty && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={removeExistingBrochure}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                )}

                {/* Show pending brochure */}
                {pendingBrochure && (
                  <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <FileText className="w-8 h-8 text-blue-600" />
                    <div className="flex-1">
                      <p className="font-medium text-blue-800">{pendingBrochure.name}</p>
                      <p className="text-sm text-blue-600">Ready to upload</p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => setPendingBrochure(null)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}

                {/* Upload button if no brochure */}
                {!formData.brochure_url && !pendingBrochure && (
                  <div 
                    onClick={() => brochureInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
                  >
                    <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-600">Click to upload brochure</p>
                    <p className="text-xs text-gray-400 mt-1">PDF or Image file</p>
                  </div>
                )}
              </div>

              {/* Commission Configuration */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-800 border-b pb-2">Commission Configuration</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-700">Commission %</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={formData.commission_percentage}
                      onChange={(e) => setFormData({ ...formData, commission_percentage: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Total Booking Fee (RM)</Label>
                    <Input
                      type="number"
                      step="100"
                      value={formData.total_booking_fee}
                      onChange={(e) => setFormData({ ...formData, total_booking_fee: e.target.value })}
                      className="mt-1"
                      placeholder="e.g., 3000 or 5000"
                      data-testid="property-booking-fee"
                    />
                    <p className="text-xs text-gray-400 mt-1">Fixed booking fee amount required for this property</p>
                  </div>
                  <div>
                    <Label className="text-gray-700">Platform Fee %</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={formData.platform_fee_percentage}
                      onChange={(e) => setFormData({ ...formData, platform_fee_percentage: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Customer Cashback %</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={formData.customer_cashback_percentage}
                      onChange={(e) => setFormData({ ...formData, customer_cashback_percentage: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Introducer Commission %</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={formData.introducer_commission_percentage}
                      onChange={(e) => setFormData({ ...formData, introducer_commission_percentage: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>

              {/* Amenities */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-800 border-b pb-2">Amenities</h3>
                <div>
                  <Label className="text-gray-700">Amenities (comma separated)</Label>
                  <Input
                    value={amenitiesInput}
                    onChange={(e) => setAmenitiesInput(e.target.value)}
                    placeholder="Swimming Pool, Gym, Parking, Playground"
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <Button 
                onClick={handleSubmit} 
                className="w-full bg-blue-600 hover:bg-blue-700" 
                disabled={uploading}
                data-testid="save-property-btn"
              >
                {uploading ? 'Saving...' : (editingProperty ? 'Update Property' : 'Create Property')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Properties Table */}
      {properties.length > 0 ? (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="font-semibold">Property</TableHead>
                <TableHead className="font-semibold">Location</TableHead>
                <TableHead className="font-semibold">Address</TableHead>
                <TableHead className="font-semibold">Type</TableHead>
                <TableHead className="font-semibold">Price Range</TableHead>
                <TableHead className="font-semibold text-center">Units</TableHead>
                <TableHead className="font-semibold text-center">Commission</TableHead>
                <TableHead className="font-semibold">Booking Form</TableHead>
                <TableHead className="font-semibold text-center">Media</TableHead>
                <TableHead className="font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {properties.map((property) => (
                <TableRow key={property.id} className="hover:bg-gray-50" data-testid={`property-row-${property.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden">
                        {property.images && property.images.length > 0 ? (
                          <img 
                            src={property.images[0].startsWith('http') ? property.images[0] : `${BACKEND_URL}${property.images[0]}`}
                            alt={property.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Building2 className="w-6 h-6 text-gray-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{property.name}</p>
                        <p className="text-xs text-gray-500">{property.developer}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-gray-600">{property.location}</TableCell>
                  <TableCell className="text-gray-500 text-sm max-w-[200px]">
                    {property.address ? (
                      <div className="flex items-start gap-1">
                        <MapPin className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />
                        <span className="line-clamp-2">{property.address}</span>
                      </div>
                    ) : (
                      <span className="text-gray-300 text-xs">No address</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">{property.property_type}</Badge>
                    <Badge className={`ml-1 text-xs ${property.is_exclusive !== false ? 'bg-[#4E50FF]/10 text-[#4E50FF] border border-[#4E50FF]/30' : 'bg-gray-100 text-gray-500 border border-gray-200'}`}>
                      {property.is_exclusive !== false ? 'Exclusive' : 'Non-Exclusive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-gray-900 font-mono text-sm">
                    {formatCurrency(property.price_range_min)} - {formatCurrency(property.price_range_max)}
                  </TableCell>
                  <TableCell className="text-center text-gray-600">{property.total_units}</TableCell>
                  <TableCell className="text-center">
                    <Badge className="bg-amber-100 text-amber-700">{property.commission_percentage}%</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {property.booking_template_id ? (
                        <div className="flex items-center gap-1">
                          <ClipboardList className="w-4 h-4 text-blue-500" />
                          <span className="text-sm text-gray-700">{getTemplateName(property.booking_template_id)}</span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">Default</span>
                      )}
                      {property.developer_template_id && (
                        <div className="flex items-center gap-1">
                          <FileText className="w-3.5 h-3.5 text-purple-500" />
                          <span className="text-xs text-purple-600">{getDeveloperTemplateName(property.developer_template_id) || 'Linked'}</span>
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex justify-center gap-1">
                      {property.images?.length > 0 && (
                        <Badge variant="outline" className="text-xs">
                          <Image className="w-3 h-3 mr-1" />
                          {property.images.length}
                        </Badge>
                      )}
                      {property.brochure_url && (
                        <Badge variant="outline" className="text-xs text-blue-600">
                          <FileText className="w-3 h-3" />
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button size="sm" variant="ghost" onClick={() => handleEdit(property)} data-testid={`edit-${property.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(property.id)} data-testid={`delete-${property.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
          <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Properties</h3>
          <p className="text-gray-500 mb-4">Add your first property to get started</p>
          <Button onClick={() => setShowModal(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Property
          </Button>
        </div>
      )}
    </div>
  );
};

export default AdminProperties;
